import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { FilterChip } from '@shared/components/data-table/data-table.component';
import { PageHeaderComponent } from '@shared/components';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { AdminPerson, PersonService } from '@features/people/services/person.service';
import { Router } from '@angular/router';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { PersonAdminDeleteDialogComponent } from './person-admin-delete-dialog.component';
import { catchError, concatMap, from, map, of, toArray } from 'rxjs';

@Component({
  selector: 'app-deleted-member-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatInputModule, MatFormFieldModule, MatSelectModule, MatDialogModule, MatCheckboxModule, MatTooltipModule, MatCardModule, PageHeaderComponent, TableSurfaceComponent, UnitSelectorComponent],
  templateUrl: './deleted-member-list.component.html',
  styleUrl: './deleted-member-list.component.scss',
})
export class DeletedMemberListComponent implements OnInit {
  readonly columns: TableColumn[] = [
    { key: 'selection', header: 'Selección', width: '80px', hideable: false, resizable: false, exportable: false },
    { key: 'name', header: 'Nombre', mobileTitle: true },
    { key: 'portalMemberId', header: 'N° ADM' },
    { key: 'documentNumber', header: 'Documento' },
    { key: 'membershipUnitName', header: 'Comunidad' },
    { key: 'status', header: 'Estado' },
    { key: 'lastModifiedAtUtc', header: 'Última edición' },
    { key: 'actions', header: 'Acciones', width: '1%', resizable: false, hideable: false, exportable: false },
  ];
  people: AdminPerson[] = [];
  readonly selectedLogicalIds = new Set<string>();
  readonly selectedPhysicalIds = new Set<string>();
  searchTerm = '';
  deletionFilter = 'all';
  selectedUnit: OrganizationUnitItem | null = null;

  get activeFilters(): FilterChip[] {
    const filters: FilterChip[] = [];
    if (this.deletionFilter !== 'all') filters.push({ key: 'status', label: this.deletionFilter === 'true' ? 'Eliminados' : 'Activos' });
    if (this.selectedUnit) filters.push({ key: 'unit', label: 'Unidad: ' + this.selectedUnit.name });
    return filters;
  }

  onUnitChanged(unit: OrganizationUnitItem | null): void {
    this.selectedUnit = unit;
    this.onDeletionFilterChanged();
  }

  removeFilter(key: string): void {
    if (key === 'unit') this.selectedUnit = null;
    if (key === 'status') this.deletionFilter = 'all';
    this.onDeletionFilterChanged();
  }
  page = 1;
  pageSize = 10;
  totalCount = 0;
  loading = false;
  busyPersonId?: string;
  error?: string;
  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.page,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: Math.max(1, Math.ceil(this.totalCount / this.pageSize)),
    };
  }

  constructor(private readonly personService: PersonService, private readonly cdr: ChangeDetectorRef, private readonly router: Router, private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = undefined;
    this.personService.getAdminPeople(this.page, this.pageSize, this.searchTerm.trim(), this.deletionFilter, this.selectedUnit?.id).subscribe({
      next: response => {
        this.people = response.items;
        this.totalCount = response.totalCount;
        this.busyPersonId = undefined;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.error = this.getErrorMessage(error, 'No se pudieron cargar los miembros.');
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  restore(person: AdminPerson): void {
    if (this.busyPersonId) return;
    this.busyPersonId = person.id;
    this.personService.restorePerson(person.id).subscribe({
      next: () => {
        this.selectedPhysicalIds.delete(person.id);
        this.load();
      },
      error: error => {
        this.error = this.getErrorMessage(error, 'No se pudo restaurar la persona.');
        this.busyPersonId = undefined;
        this.cdr.markForCheck();
      },
    });
  }

  createNew(): void { void this.router.navigate(['/people/new']); }

  viewProfile(person: AdminPerson): void { void this.router.navigate(['/people', person.id]); }

  edit(person: AdminPerson): void { void this.router.navigate(['/people', person.id, 'edit']); }

  permanentlyDelete(person: AdminPerson): void {
    if (this.busyPersonId) return;
    const fullName = `${person.firstName} ${person.lastName}`.trim();
    this.dialog.open(PersonAdminDeleteDialogComponent, modalDialogConfig({
      data: { mode: 'physical', fullName }, size: 'sm', layout: 'confirm', tone: 'danger',
    })).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.busyPersonId = person.id;
      this.personService.permanentlyDeletePerson(person.id).subscribe({
        next: () => { this.selectedPhysicalIds.delete(person.id); this.selectedLogicalIds.delete(person.id); this.load(); },
        error: error => {
          this.error = this.getErrorMessage(error, 'No se pudo eliminar definitivamente el miembro.');
          this.busyPersonId = undefined;
          this.cdr.markForCheck();
        },
      });
    });
  }

  delete(person: AdminPerson): void {
    const fullName = `${person.firstName} ${person.lastName}`.trim();
    this.dialog.open(PersonAdminDeleteDialogComponent, modalDialogConfig({
      data: { mode: 'logical', fullName }, size: 'sm', layout: 'confirm', tone: 'danger',
    })).afterClosed().subscribe(result => {
      if (!result || result === true) return;
      this.busyPersonId = person.id;
      this.personService.deletePerson(person.id, result.reason).subscribe({ next: () => { this.selectedLogicalIds.delete(person.id); this.load(); }, error: error => {
        this.error = this.getErrorMessage(error, 'No se pudo eliminar el miembro.');
        this.busyPersonId = undefined;
        this.cdr.markForCheck();
      }});
    });
  }

  changePage(page: number): void { this.page = page; this.load(); }

  changePageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
    this.load();
  }

  search(term: string): void {
    this.searchTerm = term;
    this.page = 1;
    this.load();
  }


  get selectedCount(): number { return this.selectedLogicalIds.size + this.selectedPhysicalIds.size; }
  get selectedOnPageCount(): number { return this.people.filter(person => this.isSelected(person)).length; }
  get allSelectableOnPageSelected(): boolean {
    return this.people.length > 0 && this.people.every(person => this.isSelected(person));
  }

  togglePersonSelection(person: AdminPerson, selected: boolean): void {
    const selectedIds = person.isDeleted || person.isMerged ? this.selectedPhysicalIds : this.selectedLogicalIds;
    if (selected) selectedIds.add(person.id);
    else selectedIds.delete(person.id);
  }

  isSelected(person: AdminPerson): boolean {
    return (person.isDeleted || person.isMerged ? this.selectedPhysicalIds : this.selectedLogicalIds).has(person.id);
  }

  togglePageSelection(selected: boolean): void {
    for (const person of this.people) this.togglePersonSelection(person, selected);
  }

  onDeletionFilterChanged(): void {
    this.page = 1;
    this.selectedLogicalIds.clear();
    this.selectedPhysicalIds.clear();
    this.load();
  }

  deleteSelectedLogically(): void {
    if (!this.selectedLogicalIds.size || this.loading) return;
    const ids = [...this.selectedLogicalIds];
    const count = ids.length;
    this.dialog.open(PersonAdminDeleteDialogComponent, modalDialogConfig({
      data: { mode: 'logical', fullName: `${count} miembros seleccionados`, isBulk: true }, size: 'sm', layout: 'confirm', tone: 'danger',
    })).afterClosed().subscribe(result => {
      if (!result || result === true) return;
      this.loading = true;
      from(ids).pipe(
        concatMap(id => this.personService.deletePerson(id, result.reason).pipe(map(() => true), catchError(() => of(false)))),
        toArray(),
      ).subscribe(results => {
        const failed = results.filter(success => !success).length;
        this.selectedLogicalIds.clear();
        this.load();
        if (failed) this.error = `Se eliminaron ${results.length - failed} miembros. ${failed} no se pudieron eliminar.`;
        this.cdr.markForCheck();
      });
    });
  }

  permanentlyDeleteSelected(): void {
    if (!this.selectedCount || this.loading || this.busyPersonId) return;
    const ids = [...this.selectedLogicalIds, ...this.selectedPhysicalIds];
    const count = ids.length;
    this.dialog.open(PersonAdminDeleteDialogComponent, modalDialogConfig({
      data: { mode: 'physical', fullName: `${count} miembros seleccionados`, isBulk: true }, size: 'sm', layout: 'confirm', tone: 'danger',
    })).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.loading = true;
      this.personService.permanentlyDeletePeople(ids).subscribe({
        next: () => { this.selectedPhysicalIds.clear(); this.selectedLogicalIds.clear(); this.load(); },
        error: error => {
          this.error = this.getErrorMessage(error, 'No se pudieron eliminar definitivamente los miembros seleccionados.');
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
    });
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const response = error as { error?: { detail?: string; title?: string }; message?: string };
    return response.error?.detail || response.error?.title || response.message || fallback;
  }
}
